import { cn } from 'cn'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { type Lang, useLang } from '@/hooks/use-lang'
import i18n from '@/i18n'

const LANGS: readonly Lang[] = ['en', 'zh']
const LANG_LABELS: Record<Lang, string> = { en: 'EN', zh: '中文' }

/** EN | 中文 segmented pill toggle — shared by CoachmarkCard and TaskOverlay. */
export function LangToggle() {
  const [params, setParams] = useSearchParams()
  // Resolved, not raw: the URL loses `?lang=` on every bare navigate, and a
  // pill that reads the URL alone snaps back to EN while the copy is Chinese.
  const activeLang = useLang()

  // Keep i18n in sync with the URL param. Without this, exiting a ZH lesson and
  // restarting leaves i18n on ZH while the URL reverts to EN — buttons stay ZH
  // but the toggle and JSON content correctly show EN.
  useEffect(() => {
    void i18n.changeLanguage(activeLang)
  }, [activeLang])

  const switchLang = (lang: Lang) => {
    if (lang === activeLang) return
    const next = new URLSearchParams(params)
    next.set('lang', lang)
    setParams(next, { replace: true })
    void i18n.changeLanguage(lang)
  }

  return (
    <fieldset className="m-0 flex shrink-0 overflow-hidden rounded-full border border-border p-0">
      <legend className="sr-only">Language</legend>
      {LANGS.map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => switchLang(lang)}
          aria-pressed={lang === activeLang}
          className={cn(
            'whitespace-nowrap px-2.5 py-1 text-[11px] font-semibold leading-none transition-colors',
            lang === activeLang
              ? 'bg-primary text-primary-foreground'
              : 'bg-transparent text-muted-foreground',
          )}
        >
          {LANG_LABELS[lang]}
        </button>
      ))}
    </fieldset>
  )
}
